import { useEffect, useRef, useState } from "react";

/** True when the given media query matches (SSR-safe, updates on change). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

/**
 * Scrollspy: observes the given section ids and returns the id currently in the
 * reading band. Mirrors the legacy IntersectionObserver (rootMargin biases the
 * "active" band toward the upper-middle of the viewport).
 */
export function useScrollSpy(ids: string[]): string {
  const [active, setActive] = useState(ids[0] ?? "");
  useEffect(() => {
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids.join(",")]);
  return active;
}

/**
 * True once the referenced element has scrolled into view (one-shot — stays
 * true afterward). Used to defer both data fetching and animation until the
 * element is actually on screen.
 */
export function useInView(
  ref: React.RefObject<Element | null>,
  { threshold = 0.3 }: { threshold?: number } = {},
): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold, inView]);
  return inView;
}

/**
 * Count-up. Animates 0 → target once `active` becomes true (i.e. scrolled into
 * view — the caller owns the in-view trigger so fetching and animation share
 * one gate). easeOutCubic; snaps if `target` changes after playing. Respects
 * reduced-motion (jumps to final, still only once active).
 */
export function useCountUp(
  target: number,
  active: boolean,
  { duration = 3200 }: { duration?: number } = {},
): number {
  const [value, setValue] = useState(0);
  const playedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(target);
  const reduce = usePrefersReducedMotion();

  // Keep latest target; snap if the animation already finished.
  useEffect(() => {
    targetRef.current = target;
    if (playedRef.current) setValue(target);
  }, [target]);

  useEffect(() => {
    if (!active || playedRef.current) return;
    playedRef.current = true;
    if (reduce) {
      setValue(targetRef.current);
      return;
    }
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * targetRef.current));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else setValue(targetRef.current);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, reduce, duration]);

  return value;
}
